import * as toml from "@iarna/toml";

const SCHEMA_VERSION = "opt-lc-20260503";

const CORE_KEYS = [
  "neighborscount",
  "maxbarsback",
  "usedynamicexits",
  "useemafilter",
  "usesmafilter",
  "emaperiod",
  "smaperiod",
] as const;

const FILTER_KEYS = [
  "usevolatilityfilter",
  "useregimefilter",
  "regimethreshold",
  "useadxfilter",
  "adxthreshold",
] as const;

const KERNEL_KEYS = [
  "usekernelsmoothing",
  "lookbackwindow",
  "relativeweight",
  "regressionlevel",
  "crossoverlag",
] as const;

const FEATURE_INDICES = [1, 2, 3, 4, 5] as const;
const FEATURE_SUBKEYS = ["type", "period", "multiplier"] as const;

const ALL_FLAT_KEYS = [
  ...CORE_KEYS,
  ...FILTER_KEYS,
  ...KERNEL_KEYS,
  ...FEATURE_INDICES.flatMap((i) => FEATURE_SUBKEYS.map((k) => `f${i}_${k}`)),
] as const;

export class LcSettingsParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LcSettingsParseError";
  }
}

function formatValue(value: unknown): string {
  if (typeof value === "boolean") return String(value);
  if (typeof value === "number") {
    if (Number.isInteger(value)) return String(value);
    return String(value);
  }
  if (typeof value === "string") return `"${value}"`;
  return String(value);
}

const BOOLEAN_KEY_SET = new Set([
  "usedynamicexits",
  "useemafilter",
  "usesmafilter",
  "usevolatilityfilter",
  "useregimefilter",
  "useadxfilter",
  "usekernelsmoothing",
]);

function coerce(key: string, value: unknown): unknown {
  if (BOOLEAN_KEY_SET.has(key)) return Boolean(value);
  return value;
}

export function serializeLcSettings(settings: Record<string, unknown>): string {
  let out = `schema_version = "${SCHEMA_VERSION}"\n`;

  out += "\n[core]\n";
  for (const key of CORE_KEYS) {
    out += `${key} = ${formatValue(coerce(key, settings[key]))}\n`;
  }

  out += "\n[filters]\n";
  for (const key of FILTER_KEYS) {
    out += `${key} = ${formatValue(coerce(key, settings[key]))}\n`;
  }

  out += "\n[kernel]\n";
  for (const key of KERNEL_KEYS) {
    out += `${key} = ${formatValue(coerce(key, settings[key]))}\n`;
  }

  for (const i of FEATURE_INDICES) {
    out += `\n[features.f${i}]\n`;
    out += `type = ${formatValue(settings[`f${i}_type`])}\n`;
    out += `period = ${formatValue(settings[`f${i}_period`])}\n`;
    out += `multiplier = ${formatValue(settings[`f${i}_multiplier`])}\n`;
  }

  return out;
}

export function parseLcSettings(tomlString: string): Record<string, unknown> {
  if (new Blob([tomlString]).size > 4096) {
    throw new LcSettingsParseError("File exceeds maximum size of 4 KB.");
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = toml.parse(tomlString) as Record<string, unknown>;
  } catch (e) {
    throw new LcSettingsParseError(
      `Invalid TOML: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (parsed.schema_version !== SCHEMA_VERSION) {
    throw new LcSettingsParseError(
      `Expected schema_version "${SCHEMA_VERSION}", got "${String(parsed.schema_version ?? "missing")}".`,
    );
  }

  const flat: Record<string, unknown> = {
    schema_version: parsed.schema_version,
  };

  const core = parsed.core as Record<string, unknown> | undefined;
  if (!core) throw new LcSettingsParseError("Missing [core] section.");
  for (const key of CORE_KEYS) {
    if (!(key in core)) {
      throw new LcSettingsParseError(`Missing key: core.${key}`);
    }
    flat[key] = core[key];
  }

  const filters = parsed.filters as Record<string, unknown> | undefined;
  if (!filters) throw new LcSettingsParseError("Missing [filters] section.");
  for (const key of FILTER_KEYS) {
    if (!(key in filters)) {
      throw new LcSettingsParseError(`Missing key: filters.${key}`);
    }
    flat[key] = filters[key];
  }

  const kernel = parsed.kernel as Record<string, unknown> | undefined;
  if (!kernel) throw new LcSettingsParseError("Missing [kernel] section.");
  for (const key of KERNEL_KEYS) {
    if (!(key in kernel)) {
      throw new LcSettingsParseError(`Missing key: kernel.${key}`);
    }
    flat[key] = kernel[key];
  }

  const features = parsed.features as
    | Record<string, Record<string, unknown>>
    | undefined;
  if (!features) {
    throw new LcSettingsParseError("Missing [features] section.");
  }

  for (const i of FEATURE_INDICES) {
    const fi = features[`f${i}`];
    if (!fi) {
      throw new LcSettingsParseError(`Missing [features.f${i}] section.`);
    }
    for (const subkey of FEATURE_SUBKEYS) {
      if (!(subkey in fi)) {
        throw new LcSettingsParseError(`Missing key: features.f${i}.${subkey}`);
      }
      flat[`f${i}_${subkey}`] = fi[subkey];
    }
  }

  // Reject unknown keys at each level
  const knownTopLevel = new Set([
    "schema_version",
    "core",
    "filters",
    "kernel",
    "features",
  ]);
  for (const key of Object.keys(parsed)) {
    if (!knownTopLevel.has(key)) {
      throw new LcSettingsParseError(`Unknown top-level key: "${key}"`);
    }
  }

  const coreKeySet = new Set<string>(CORE_KEYS);
  for (const key of Object.keys(core)) {
    if (!coreKeySet.has(key)) {
      throw new LcSettingsParseError(`Unknown key in [core]: "${key}"`);
    }
  }

  const filterKeySet = new Set<string>(FILTER_KEYS);
  for (const key of Object.keys(filters)) {
    if (!filterKeySet.has(key)) {
      throw new LcSettingsParseError(`Unknown key in [filters]: "${key}"`);
    }
  }

  const kernelKeySet = new Set<string>(KERNEL_KEYS);
  for (const key of Object.keys(kernel)) {
    if (!kernelKeySet.has(key)) {
      throw new LcSettingsParseError(`Unknown key in [kernel]: "${key}"`);
    }
  }

  const featureSubkeySet = new Set<string>(FEATURE_SUBKEYS);
  const featureNameSet = new Set(FEATURE_INDICES.map((i) => `f${i}`));
  for (const fname of Object.keys(features)) {
    if (!featureNameSet.has(fname)) {
      throw new LcSettingsParseError(
        `Unknown feature in [features]: "${fname}"`,
      );
    }
    for (const subkey of Object.keys(
      features[fname] as Record<string, unknown>,
    )) {
      if (!featureSubkeySet.has(subkey)) {
        throw new LcSettingsParseError(
          `Unknown key in [features.${fname}]: "${subkey}"`,
        );
      }
    }
  }

  // Type, range, and enum validation
  const VALID_FEATURE_TYPES = new Set(["RSI", "WT", "CCI", "ADX"]);

  const BOOLEAN_KEYS = new Set([
    "usedynamicexits",
    "useemafilter",
    "usesmafilter",
    "usevolatilityfilter",
    "useregimefilter",
    "useadxfilter",
    "usekernelsmoothing",
  ]);

  const INT_KEYS: Record<string, { min: number; max: number }> = {
    neighborscount: { min: 1, max: 200 },
    maxbarsback: { min: 10, max: 40000 },
    emaperiod: { min: 1, max: Infinity },
    smaperiod: { min: 1, max: Infinity },
    lookbackwindow: { min: 1, max: 100 },
    regressionlevel: { min: 1, max: 100 },
    crossoverlag: { min: 1, max: 20 },
    adxthreshold: { min: 0, max: 100 },
  };

  const FLOAT_KEYS: Record<string, { min: number; max: number }> = {
    regimethreshold: { min: -10, max: 10 },
    relativeweight: { min: 0.1, max: Infinity },
  };

  for (const key of BOOLEAN_KEYS) {
    if (flat[key] === 0) flat[key] = false;
    if (flat[key] === 1) flat[key] = true;
    if (typeof flat[key] !== "boolean") {
      throw new LcSettingsParseError(
        `"${key}" must be a boolean, got ${typeof flat[key]}.`,
      );
    }
  }

  for (const [key, { min, max }] of Object.entries(INT_KEYS)) {
    const v = flat[key];
    if (typeof v !== "number" || !Number.isFinite(v)) {
      throw new LcSettingsParseError(
        `"${key}" must be a number, got ${typeof v}.`,
      );
    }
    if (!Number.isInteger(v)) {
      throw new LcSettingsParseError(`"${key}" must be an integer, got ${v}.`);
    }
    if (v < min || v > max) {
      throw new LcSettingsParseError(
        `"${key}" = ${v} is out of range [${min}, ${max}].`,
      );
    }
  }

  for (const [key, { min, max }] of Object.entries(FLOAT_KEYS)) {
    const v = flat[key];
    if (typeof v !== "number" || !Number.isFinite(v)) {
      throw new LcSettingsParseError(
        `"${key}" must be a number, got ${typeof v}.`,
      );
    }
    if (v < min || v > max) {
      throw new LcSettingsParseError(
        `"${key}" = ${v} is out of range [${min}, ${max}].`,
      );
    }
  }

  for (const i of FEATURE_INDICES) {
    const typeVal = flat[`f${i}_type`];
    if (typeof typeVal !== "string" || !VALID_FEATURE_TYPES.has(typeVal)) {
      throw new LcSettingsParseError(
        `"f${i}_type" must be one of ${[...VALID_FEATURE_TYPES].join(", ")}, got "${String(typeVal)}".`,
      );
    }

    for (const sk of ["period", "multiplier"] as const) {
      const v = flat[`f${i}_${sk}`];
      if (
        typeof v !== "number" ||
        !Number.isFinite(v) ||
        !Number.isInteger(v) ||
        v < 1
      ) {
        throw new LcSettingsParseError(
          `"f${i}_${sk}" must be an integer >= 1, got ${typeof v === "number" ? v : typeof v}.`,
        );
      }
    }
  }

  return flat;
}

export { ALL_FLAT_KEYS, SCHEMA_VERSION };
